/**
 * What a spotlight's button does (spec cova-feed/2026-10-09-001 §5.1).
 *
 * ═══ THE ACTION IS DATA, NOT A URL ═══
 *
 * Four typed kinds. None is a free-text URL into the app: an in-app screen is
 * a destination ID from `src/shared/config/app-destinations.ts`, so a renamed
 * route is fixed once there and the catalogue can say what the screen is gated
 * on. The shapes are plain and closed so the Customer Success agent can later
 * emit them as JSON and the service validates them exactly as it validates the
 * composer's.
 *
 * `destinationId` is a plain string on the wire, not the catalogue's literal
 * union: a stored spotlight outlives a removed screen, and the admin service
 * refuses an unknown id with a content-free error rather than a payload decode
 * failure that would echo the value.
 *
 * Frontend-safe.
 */
import { Schema as S } from "effect"

export const SpotlightAction = S.Union(
  /** A screen in Cova, by catalogue id. */
  S.Struct({ kind: S.Literal("route"), destinationId: S.String }),
  /** Opens the agent with this message typed in, never sent. */
  S.Struct({ kind: S.Literal("agent"), prompt: S.String }),
  /** Docs or a video, in a new tab. https only, checked by the service. */
  S.Struct({ kind: S.Literal("external"), url: S.String }),
  /** The feed post itself, when there is nowhere better to send them. */
  S.Struct({ kind: S.Literal("post"), postId: S.String }),
)
export type SpotlightAction = typeof SpotlightAction.Type
export type SpotlightActionKind = SpotlightAction["kind"]

/** What the popup does for a viewer the action's gate keeps out: ask, or skip them. */
export const NoAccessMode = S.Literal("request", "skip")
export type NoAccessMode = typeof NoAccessMode.Type

export const CtaSpec = S.Struct({
  label: S.String,
  action: SpotlightAction,
  noAccess: NoAccessMode,
})
export type CtaSpec = typeof CtaSpec.Type
